import React, { useRef, useEffect, useState } from 'react';
import { Box, Text, Input, Button, HStack, IconButton, Portal } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Message, Reaction, User } from '../types';
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { FaRegSmile, FaRegGrin } from "react-icons/fa";
import { useChat } from '../ChatProvider';
import UserAvatar from '../../../components/UserAvatar';

const MotionBox = motion.create(Box);
const MotionHStack = motion.create(HStack);

interface ChatMessagesProps {
  messages: Message[];
  selectedRoom: string;
  currentUserId: string | null;
  newMessage: string;
  onNewMessageChange: (message: string) => void;
  onSendMessage: (e: React.FormEvent) => void;
  bgColor: string;
  messageBg: string;
  userMessageBg: string;
  textColor: string;
  secondaryText: string;
  inputBg: string;
  borderColor: string;
  PRIMARY_GREEN: string;
  isGroupChat: boolean;
  groupChatMembers: User[];
  isSupportChat: boolean;
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({
  messages,
  selectedRoom,
  currentUserId,
  newMessage,
  onNewMessageChange,
  onSendMessage,
  bgColor,
  messageBg,
  userMessageBg,
  textColor,
  secondaryText,
  inputBg,
  borderColor,
  PRIMARY_GREEN,
  isGroupChat,
  groupChatMembers,
  isSupportChat
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null);
  const [pickerPosition, setPickerPosition] = useState({ x: 0, y: 0 });
  const { socket } = useChat();

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const onEmojiClick = (emojiObject: any) => {
    onNewMessageChange(newMessage + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  const onReactionClick = (emojiObject: any, messageId: string) => {
    if (!socket || !currentUserId) return;

    socket.emit('message:reaction', {
      messageId,
      emoji: emojiObject.emoji,
      userId: currentUserId
    });

    setShowReactionPicker(null);
  };

  const getReactionCount = (reactions: Reaction[] | undefined, emoji: string) => {
    if (!reactions) return 0;
    return reactions.filter(r => r.emoji === emoji).length;
  };

  const getUniqueReactions = (reactions: Reaction[] | undefined) => {
    if (!reactions) return [];
    return Array.from(new Set(reactions.map(r => r.emoji)));
  };

  const handleContextMenu = (e: React.MouseEvent, messageId: string) => {
    e.preventDefault();
    setPickerPosition({ x: e.clientX, y: e.clientY });
    setShowReactionPicker(messageId);
  };

  const handleReactionClick = (emoji: string, messageId: string) => {
    onReactionClick({ emoji }, messageId);
  };

  // Close pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showReactionPicker) {
        setShowReactionPicker(null);
      }
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(e.target as Node) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(e.target as Node)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showReactionPicker, showEmojiPicker]);

  return (
    <Box display="flex" flexDirection="column" h="100%" minH="0">
      <Box
        ref={messagesContainerRef}
        flex="1"
        overflowY="auto"
        p={4}
        bg={messageBg}
        minH="0"
        display="flex"
        flexDirection="column"
        gap={4}
      >
        {messages
          .filter((msg) => msg.chat_room_id === selectedRoom)
          .map((message, index, filteredMessages) => {
            // Check if this is the first message from this sender in a sequence
            const isFirstInSequence = index === 0 ||
              filteredMessages[index - 1].sender.id !== message.sender.id;

            return (
              <MotionBox
                key={message.id}
                alignSelf={
                  message.sender.id === currentUserId ? 'flex-end' : 'flex-start'
                }
                maxW="80%"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                {isGroupChat && groupChatMembers && !isSupportChat &&
                  groupChatMembers.find((member: User) => member.id === message.sender.id) &&
                  isFirstInSequence && (
                    <HStack mb={1}>
                      <UserAvatar user={groupChatMembers.find((member: User) => member.id === message.sender.id)!} canEdit={false} size="sm" />
                      <Text fontSize="xs" color={secondaryText}>
                        {groupChatMembers.find((member: User) => member.id === message.sender.id)?.username}
                      </Text>
                    </HStack>
                  )}
                <Box
                  bg={
                    message.sender.id === currentUserId
                      ? userMessageBg
                      : bgColor
                  }
                  color={
                    message.sender.id === currentUserId
                      ? textColor
                      : secondaryText
                  }
                  p={3}
                  borderRadius="lg"
                  position="relative"
                  cursor="default"
                >
                  <Text fontSize="sm">
                    {message.content}
                  </Text>
                  <Text
                    fontSize="xs"
                    mt={1}
                    color={secondaryText}
                  >
                    {format(new Date(message.created_at), 'h:mm a')}
                  </Text>

                  {/* Reaction button */}
                  {message.sender.id !== currentUserId && (
                    <IconButton
                      aria-label="Add reaction"
                      icon={<FaRegGrin />}
                      size="xs"
                      variant="ghost"
                      position="absolute"
                      bottom={1}
                      right="-6"
                      opacity={0.6}
                      _hover={{ opacity: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPickerPosition({ x: e.clientX, y: e.clientY });
                        setShowReactionPicker(message.id);
                      }}
                      color={secondaryText}
                      bg="transparent"
                      minW="auto"
                      h="auto"
                      p={1}
                    />
                  )}

                  {message.reactions && message.reactions.length > 0 && (
                    <AnimatePresence>
                      <MotionHStack
                        mt={2}
                        gap={1}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {getUniqueReactions(message.reactions).map((emoji) => (
                          <MotionBox
                            key={emoji}
                            bg={bgColor}
                            px={2}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                            color={textColor}
                            onClick={() => { message.sender.id !== currentUserId ? handleReactionClick(emoji, message.id) : null }}
                            cursor={message.sender.id !== currentUserId ? "pointer" : "default"}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            {emoji} {getReactionCount(message.reactions, emoji)}
                          </MotionBox>
                        ))}
                      </MotionHStack>
                    </AnimatePresence>
                  )}
                </Box>
              </MotionBox>
            );
          })}
        <div ref={messagesEndRef} />
      </Box>

      <Box p={4} borderTopWidth="1px" borderColor={borderColor} bg={bgColor} borderBottomRadius="2xl" position="relative">
        <form id="chat-form" onSubmit={onSendMessage}>
          <HStack>
            <Input
              value={newMessage}
              onChange={(e) => onNewMessageChange(e.target.value)}
              placeholder="Type a message..."
              size="md"
              bg={inputBg}
              color={textColor}
              borderRadius="lg"
              _placeholder={{ color: secondaryText }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSendMessage(e as any);
                }
              }}
            />
            <IconButton
              ref={emojiButtonRef}
              aria-label="Add emoji"
              bg="transparent"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              variant="ghost"
              color={textColor}
            >
              <FaRegSmile />
            </IconButton>
            <Button
              type="submit"
              colorScheme="green"
              size="md"
              bg={PRIMARY_GREEN}
              color={textColor}
              borderRadius="lg"
              _hover={{ bg: '#004d1a' }}
            >
              Send
            </Button>
          </HStack>
        </form>
        {showEmojiPicker && (
          <Box ref={emojiPickerRef} position="absolute" bottom="12%" right="0" zIndex={10}>
            <EmojiPicker theme={Theme.DARK} onEmojiClick={onEmojiClick} />
          </Box>
        )}
        {showReactionPicker && (
          <Portal>
            <Box
              position="fixed"
              right={window.innerWidth - pickerPosition.x}
              bottom={window.innerHeight - pickerPosition.y}
              zIndex={1000}
              onClick={(e) => e.stopPropagation()}
            >
              <EmojiPicker
                reactionsDefaultOpen={true}
                theme={Theme.DARK}
                onEmojiClick={(emojiObject) => onReactionClick(emojiObject, showReactionPicker)}
              />
            </Box>
          </Portal>
        )}
      </Box>
    </Box>
  );
}; 